<template>
  <div class="running-mode">
    <div class="side-bar" v-show="showSideBar">
      <div class="upper-buttons">
        <button
          class="side-button"
          :class="{ mainOn: showMain, mainOff: !showMain }"
          @click="clickHome"
        ></button>
        <button
          class="side-button"
          :class="{ patientOn: showConfig, patientOff: !showConfig }"
          @click="clickPerson"
        ></button>
      </div>
      <div class="lower-buttons">
        <button
          class="side-button"
          :class="{ backOn: showConfirmExit, backOff: !showConfirmExit }"
          @click="backClick"
        ></button>
      </div>
    </div>
    <RunningModeMain
      :style="{ visibility: showMain ? 'visible' : 'hidden' }"
      class="main"
      :endTime="endTime"
      :co="coLast5Mins"
      :hr="hrLast5Mins"
      :svr="svrLast5Mins"
      :trendsXMax="trendsXMax"
      :trendsXMin="trendsXMin"
      :cumulatives="cumulatives"
      :mapTrend="mapTrend"
      :systolicBP="systolicBP"
      :diastolicBP="diastolicBP"
      :meanBP="meanBP"
      :heartRate="heartRate"
      :markers="treatments"
    ></RunningModeMain>
    <RunningModeConfig
      v-show="showConfig"
      class="main"
      @patientWeightChanged="patientWeightChanged"
      @patientHeightChanged="patientHeightChanged"
      @patientAgeChanged="patientAgeChanged"
      @surgeryTypeChanged="surgeryTypeChanged"
    ></RunningModeConfig>
    <CaseReport
      class="case-report"
      v-show="showCaseReview"
      :surgeryType="surgeryType"
      :startTime="caseStartTime"
      :endTime="caseEndTime"
      :sessionNumber="sessionNumber"
      :mapSignal="mapSignal"
      :trendSignals="trendSignals"
      :xMin="xMin"
      :xMax="xMax"
      :treatments="treatments"
      :title="caseTitle"
      :age="age"
      :height="height"
      :weight="weight"
      @exitCaseReview="exitCaseReview"
    >
    </CaseReport>
    <ConfirmExit
      v-if="showConfirmExit"
      @close-dialog="closeDialogs"
      @end-session="endSession"
      class="exit-dialog"
    ></ConfirmExit>
  </div>
</template>

<script>
import Papa from 'papaparse'
import RunningModeMain from '../views/RunningModeMain'
import RunningModeConfig from '../views/RunningModeConfig'
import ConfirmExit from '../components/ConfirmExit'
import CaseReport from '../views/CaseReport'

export default {
  name: 'RunningMode',
  components: {
    RunningModeMain,
    RunningModeConfig,
    ConfirmExit,
    CaseReport
  },
  data() {
    return {
      showMain: true,
      showConfig: false,
      showConfirmExit: false,
      showSideBar: true,
      showCaseReview: false,
      timer: null,
      mapTrend: [],
      cumulatives: [],
      co: [],
      hr: [],
      svr: [],
      coLast5Mins: [],
      hrLast5Mins: [],
      svrLast5Mins: [],
      xMin: 0,
      xMax: 100,
      trendsXMax: 100,
      trendsXMin: 0,
      caseStartTime: null,
      caseEndTime: null,
      startTime: null,
      endTime: null,
      treatments: [],
      caseTitle: '',
      sessionNumber: '',
      age: '',
      height: '',
      weight: '',
      surgeryType: ''
    }
  },
  methods: {
    patientWeightChanged(value) {
      this.weight = value
    },
    patientHeightChanged(value) {
      this.height = value
    },
    patientAgeChanged(value) {
      this.age = value
    },
    surgeryTypeChanged(value) {
      this.surgeryType = value
    },
    closeDialogs() {
      this.showConfirmExit = false
    },
    endSession() {
      this.showMain = false
      this.showConfig = false
      clearTimeout(this.timer)
      this.showConfirmExit = false
      this.showSideBar = false
      this.showCaseReview = true
    },
    exitCaseReview() {
      this.$emit('stop-patient-session')
    },
    clickHome() {
      this.showMain = true
      this.showConfig = false
    },
    clickPerson() {
      this.showMain = false
      this.showConfig = true
    },
    backClick() {
      this.showConfirmExit = true
    },
    numberCheck(x) {
      if (typeof x != 'number') {
        return NaN
      } else {
        return x
      }
    },
    nanSafe(x) {
      if (isFinite(x)) {
        return x
      } else {
        return '-'
      }
    },
    updateCumulatives() {
      let bpTargetMax = this.$store.state.bpTargetMax
      let bpTargetMin = this.$store.state.bpTargetMin
      let mildHypoThreshold = this.$store.state.mildHypoThreshold
      let targetDuration_s = 0
      let mildHypoDuration_s = 0
      let severeHypoDuration_s = 0
      this.mapTrend.forEach(element => {
        if (element[1] < 55) {
          severeHypoDuration_s += 1
        }
        if (element[1] > 55 && element[1] < mildHypoThreshold) {
          mildHypoDuration_s += 1
        }
        if (element[1] > bpTargetMin && element[1] < bpTargetMax) {
          targetDuration_s += 1
        }
      })
      let targetDuration = (targetDuration_s / 60).toFixed(1)
      let mildHypoDuration = (mildHypoDuration_s / 60).toFixed(1)
      let severeHypoDuration = (severeHypoDuration_s / 60).toFixed(1)
      this.cumulatives = [
        [`${bpTargetMin} - ${bpTargetMax} mmHg`, `${targetDuration} mins`],
        [`55 - ${mildHypoThreshold} mmHg`, `${mildHypoDuration} mins`],
        [`< 55 mmHg`, `${severeHypoDuration} mins`]
      ]
    },
    checkTime(i) {
      if (i < 10) {
        i = '0' + i
      } // add zero in front of numbers < 10
      return i
    },
    getTimestamp(date) {
      let day = date.getDay()
      let month = date.toLocaleString('default', { month: 'short' })
      let year = date.getFullYear()
      let h = date.getHours()
      let m = date.getMinutes()
      m = this.checkTime(m)
      return day + ' ' + month + ' ' + year + '  ' + h + ':' + m
    },
    updatePlot(data, idx) {
      this.mapTrend.push([
        idx,
        this.numberCheck(data[idx % data.length]['MAP'])
      ])
      this.signal = this.mapTrend
      this.co.push([idx, data[idx % data.length]['nCO']])
      this.hr.push([idx, data[idx % data.length]['HR']])
      this.svr.push([idx, data[idx % data.length]['SVR']])
      this.coLast5Mins = this.co.slice(
        this.co.length - 5 * 60,
        this.co.length - 1
      )
      this.hrLast5Mins = this.hr.slice(
        this.hr.length - 5 * 60,
        this.hr.length - 1
      )
      this.svrLast5Mins = this.svr.slice(
        this.svr.length - 5 * 60,
        this.svr.length - 1
      )
      this.systolicBP = this.nanSafe(
        Math.round(data[idx % data.length]['SystolicBP'])
      )
      this.diastolicBP = this.nanSafe(
        Math.round(data[idx % data.length]['DiastolicBP'])
      )
      this.meanBP = this.nanSafe(Math.round(data[idx % data.length]['MAP']))
      this.heartRate = this.nanSafe(Math.round(data[idx % data.length]['HR']))
      this.updateCumulatives()
      this.endTime = new Date(this.endTime.getTime() + 1000)
      this.trendsXMax = idx
      this.trendsXMin = idx - 5 * 60
      this.signalQualityGood = this.meanBP !== '-'
      this.$store.commit('setTimestamp', this.getTimestamp(this.endTime))
      idx++
      this.timer = setTimeout(() => {
        this.updatePlot(data, idx)
      }, 1000)
    }
  },
  mounted() {
    // Parse local CSV file
    Papa.parse('data.csv', {
      download: true,
      header: true,
      dynamicTyping: true,
      complete: function(results) {
        // Load 15mins worth
        const steps = 15 * 60 // 15 mins x 60s
        let map = []
        this.co = []
        this.hr = []
        this.svr = []
        let i = 0
        for (i = 0; i < steps; i++) {
          map.push([i, this.numberCheck(results.data[i]['MAP'])])
          this.co.push([i, results.data[i]['nCO']])
          this.hr.push([i, results.data[i]['HR']])
          this.svr.push([i, results.data[i]['SVR']])
        }
        this.coLast5Mins = this.co.slice(
          this.co.length - 5 * 60,
          this.co.length - 1
        )
        this.hrLast5Mins = this.hr.slice(
          this.hr.length - 5 * 60,
          this.hr.length - 1
        )
        this.svrLast5Mins = this.svr.slice(
          this.svr.length - 5 * 60,
          this.svr.length - 1
        )
        this.trendsXMax = i
        this.trendsXMin = i - 5 * 60
        this.mapTrend = map
        this.signal = map
        this.trendSignal = this.co
        this.systolicBP = Math.round(results.data[steps]['SystolicBP'])
        this.diastolicBP = Math.round(results.data[steps]['DiastolicBP'])
        this.meanBP = Math.round(results.data[steps]['MAP'])
        this.heartRate = Math.round(results.data[steps]['HR'])
        // Set startTime to 15mins before actual now
        let start = new Date()
        start.setMinutes(start.getMinutes() - 15)
        this.startTime = start
        this.caseStartTime = start
        // Set endTime to actual now
        this.endTime = new Date()
        this.caseEndTime = this.endTime
        this.updateCumulatives()
        this.timer = setTimeout(() => {
          this.updatePlot(results.data, i)
        }, 1000)
      }.bind(this)
    })
  },
  beforeDestroy() {
    clearTimeout(this.timer)
  },
  computed: {
    mapSignal() {
      return [
        {
          points: this.mapTrend,
          name: 'MAP',
          color: '#00413B'
        }
      ]
    },
    trendSignals() {
      return [
        {
          points: this.co,
          name: 'CO',
          color: '#1B77D2'
        },
        {
          points: this.hr,
          name: 'HR',
          color: '#fcbc36'
        },
        {
          points: this.svr,
          name: 'SVR',
          color: '#d345da'
        }
      ]
    }
  }
}
</script>

<style scoped>
.running-mode {
  display: grid;
  background-color: #add4c8;
  margin: 0;
  grid-template-columns: 5.1% 94.9%;
  grid-template-rows: 100%;
}
.side-bar {
  grid-row: 1;
  grid-column: 1;
  background-color: white;
  position: relative;
}
.upper-buttons {
  position: absolute;
  width: 100%;
  left: 0;
  top: 0;
}
.lower-buttons {
  position: absolute;
  width: 100%;
  left: 0;
  bottom: 0;
}
.side-button {
  width: 80%;
  padding-top: 80%;
  margin: 10%;
  background-size: 100%;
  background-repeat: no-repeat;
  background-position: center;
  border: none;
  background-color: transparent;
}
.side-button:focus {
  outline: none;
}
.patientOn {
  background-image: url('../assets/patient-on.svg');
}
.patientOff {
  background-image: url('../assets/patient-off.svg');
}
.mainOn {
  background-image: url('../assets/main-on.svg');
}
.mainOff {
  background-image: url('../assets/main-off.svg');
}
.backOff {
  background-image: url('../assets/stop-hand.svg');
}
.backOn {
  background-image: url('../assets/stop-hand-on.svg');
}
.main {
  grid-row: 1;
  grid-column: 2;
}
.case-report {
  grid-row: 1;
  grid-column-start: 1;
  grid-column-end: 3;
}
.exit-dialog {
  grid-row: 1;
  grid-column: 2;
  top: 17%;
  left: 7%;
  width: 65%;
  height: 60%;
  z-index: 3;
}
</style>
